// --- EFECTO MÁQUINA DE ESCRIBIR (hero title) ---
// Solo se activa en modo preview (!store.get('editorMode'))
import { store } from '../core/store.js';
import { registerPostRender } from '../core/render.js';

const TYPING_SPEED = 45;   // ms por carácter (más bajo = más rápido)
const CURSOR_BLINK  = 530; // ms del parpadeo del cursor

/**
 * Marca que este titular lo gobierna el tecleo. SSOT: la exporta este módulo y la consume
 * variants/hero.js para no tocar el <h1> que se está escribiendo.
 *
 * Antes cada lado usaba su propio literal —aquí `typedDone`, allí `data-typed`— y como el
 * segundo no lo escribía nadie, la comprobación del hero era SIEMPRE falsa: heroV2 apagaba
 * el titular (opacity:0) para animar los caracteres de splitText, el tecleo los destruía al
 * reescribir el <h1>, y el elemento se quedaba invisible hasta que la red de rescate lo
 * destapaba 6 s después (el texto "aparecía de golpe", ya escrito y sin animación).
 * En dataset se escribe camelCase; en el DOM queda como `data-typed-done`.
 */
export const TYPED_DONE = 'typedDone';

/**
 * Los nodos de TEXTO del titular, en orden de lectura, con el texto que tienen AHORA.
 * El tecleo escribe DENTRO de ellos: la estructura del titular no se toca.
 */
function nodosDeTexto(el) {
    const out = [];
    const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = w.nextNode(); n; n = w.nextNode()) out.push({ nodo: n, texto: n.nodeValue || '' });
    return out;
}

/**
 * Anima el titular escribiéndolo carácter a carácter SIN rehacer su HTML: vacía sus nodos de texto
 * y los vuelve a llenar en orden. Antes se quedaba con el texto plano (`textContent`), vaciaba el
 * `innerHTML` y reescribía solo el texto: en «Ver sitio» y en lo publicado se perdía todo lo que el
 * cliente le hubiera dado al titular —la alineación (`<span data-align>`, menú contextual), una
 * negrita, un color, un salto de línea—, y como el extractor lee el DOM, el autoguardado o un cambio
 * de página en «Ver sitio» dejaban esa pérdida GUARDADA (25-09-2026).
 * @param {HTMLElement} el     - El h1 a animar
 * @param {Function}    onDone - Callback cuando termina
 */
function typeText(el, onDone) {
    // Si un tecleo anterior dejó su cursor (el hero se repintó a mitad) se quita antes de nada; si
    // quedó FUERA del titular por un clon de split-text, también.
    el.querySelectorAll('.typed-cursor').forEach(c => c.remove());
    el.parentElement && el.parentElement.querySelectorAll('.typed-cursor').forEach(c => c.remove());

    // Respaldo para el extractor mientras dura el tecleo: el HTML ENTERO del titular, con su
    // formato (antes el texto plano: un guardado a mitad de la animación se llevaba el formato).
    el.setAttribute('data-val-anim', el.innerHTML);

    const textos = nodosDeTexto(el);
    textos.forEach(t => { t.nodo.nodeValue = ''; });
    const restaurar = () => textos.forEach(t => { t.nodo.nodeValue = t.texto; });

    // El cursor va detrás del nodo que se está escribiendo: dentro de su mismo elemento (el span
    // de la alineación, una negrita…), así hereda su color y su sitio.
    const cursor = document.createElement('span');
    cursor.className = 'typed-cursor';
    cursor.textContent = '|';
    let k = 0;   // nodo que se escribe
    let i = 0;   // caracteres ya escritos de ese nodo
    const ponerCursor = () => {
        const t = textos[k];
        if (t && t.nodo.parentNode) t.nodo.parentNode.insertBefore(cursor, t.nodo.nextSibling);
        else el.appendChild(cursor);
    };
    ponerCursor();

    const interval = setInterval(() => {
        // Si data-val-anim fue removido externamente (el Guardar del creador lo quita), abortar
        // dejando el titular ENTERO (sin el texto a medias) y sin el cursor.
        if (!el.hasAttribute('data-val-anim')) {
            clearInterval(interval);
            cursor.remove();
            restaurar();
            if (onDone) onDone();
            return;
        }
        while (k < textos.length && i >= textos[k].texto.length) { k++; i = 0; ponerCursor(); }
        if (k < textos.length) {
            i++;
            textos[k].nodo.nodeValue = textos[k].texto.slice(0, i);
            return;
        }
        clearInterval(interval);
        // El atributo de respaldo se quita DESPUÉS de retirar el cursor, no antes. Se quitaba
        // aquí, con el cursor aún 1,9 s dentro del <h1>: si en esa ventana el extractor leía el
        // titular (autoguardado, Guardar, cambio de página) ya no veía `data-val-anim` y
        // guardaba el innerHTML CON el <span class="typed-cursor">|</span>; el título se
        // publicaba así y cada visita tecleaba «…|» (producción, 18-09-2026; y el sitio 12 de
        // test222 lo tiene horneado). Mientras el cursor exista, el extractor lee el HTML limpio.
        setTimeout(() => {
            cursor.style.animation = 'typedFadeOut 0.4s forwards';
            setTimeout(() => { cursor.remove(); el.removeAttribute('data-val-anim'); }, 400);
        }, 1500);
        if (onDone) onDone();
    }, TYPING_SPEED);
}

/**
 * Punto de entrada principal.
 * Busca el h1 del hero y lo anima si estamos en preview.
 */
export function initTypedEffect() {
    // No activar en modo editor (interfiere con el contenteditable)
    if (store.get('editorMode')) return;

    // Ni MIENTRAS SE CONSTRUYE el sitio. En el creador `editorMode` es false, así que este hook
    // corría 150 ms después de cada render — encima del tecleo de `pages/creator/typewrite.js`,
    // que está escribiendo ese mismo `<h1>`. Dos animaciones peleándose por un elemento dejaban el
    // título a medias o vacío. Aquí no se pierde nada: cuando la generación termina hay un render
    // final y el efecto entra entonces, sobre el hero ya definitivo.
    if (store.get('isGenerating')) return;

    const heroTitle = document.querySelector('#bloque-hero [data-field="title"]');
    if (!heroTitle || heroTitle.dataset[TYPED_DONE]) return;

    if (!(heroTitle.textContent || '').trim()) return;

    // Marcar para no repetir si renderPage se llama de nuevo. Se hace ANTES del setTimeout, y
    // este hook se registra antes que el de las animaciones (ver el orden de imports en
    // script.js y public/public-init.js), así que cuando corre la coreografía del hero la
    // marca ya está puesta y la respeta.
    heroTitle.dataset[TYPED_DONE] = 'true';

    // Pequeño delay para que el reveal animation del hero termine primero
    setTimeout(() => {
        typeText(heroTitle);
    }, 300);
}

registerPostRender(initTypedEffect, { previewOnly: true });
